import { useState } from 'react';
import { AlertForm, AlertFormProps, JFormik } from '@centerpiece/components';
import { FormikHelpers, getIn } from 'formik';
import * as Yup from 'yup';
import {
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  useGetProjectStagesQuery,
  useDeleteProjectStageMutation,
  useUpdateProjectStageMutation,
  useCreateProjectStageMutation,
  useCreateOutcomeMutation,
  usePatchOutcomeMutation,
  useDeleteOutcomeMutation,
  useGetProjectStatesQuery,
  useCreateProjectStateMutation,
  useDeleteProjectStateMutation,
  useUpdateProjectStateMutation,
  useCreateProjectStateReasonMutation,
  useUpdateProjectStateReasonMutation,
  useDeleteProjectStateReasonMutation,
} from 'store/api';
import { InfoOutlined } from '@mui/icons-material';
import { useGetWorkFlowForm } from 'utils/workflow.utils';
import { Stage } from 'store/api/models';

export const ProjectStagesTab = () => {
  const [editing /*, setEditing*/] = useState<boolean>(false);
  const {
    data: stages = [],
    isLoading: isStagesLoading,
    refetch: refetchStages,
  } = useGetProjectStagesQuery({ params: { nested: 'all' } });
  const {
    data: states = [],
    isLoading: isStatesLoading,
    refetch: refetchStates,
  } = useGetProjectStatesQuery({ params: { nested: 'all' } });
  const [createProjectState] = useCreateProjectStateMutation();
  const [editProjectState] = useUpdateProjectStateMutation();
  const [deleteProjectState] = useDeleteProjectStateMutation();
  const [createProjectStage] = useCreateProjectStageMutation();
  const [editProjectStage] = useUpdateProjectStageMutation();
  const [deleteProjectStage] = useDeleteProjectStageMutation();
  const [createOutcome] = useCreateOutcomeMutation({});
  const [editOutcome] = usePatchOutcomeMutation();
  const [deleteOutcome] = useDeleteOutcomeMutation();
  const [createReason] = useCreateProjectStateReasonMutation();
  const [editReason] = useUpdateProjectStateReasonMutation();
  const [deleteReason] = useDeleteProjectStateReasonMutation();
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [workflowDetails, setWorkflowDetails] = useState<AlertFormProps>({
    open: false,
  });
  const getWorkflowForm = useGetWorkFlowForm({ setOpenContact: () => {} });
  return (
    <>
      <AlertForm {...workflowDetails} />
      <JFormik
        initialValues={() => ({
          states: states,
          stages: stages
            .filter(s => s.subscription)
            .map(s => ({
              ...s,
              outcomes: s?.outcomes?.map(o => ({ ...o, state: o.state_r })),
            })),
        })}
        enableReinitialize={true}
        handleSubmit={async (values, formik) => {}}
        loading={isStatesLoading}
        validationSchema={
          editing
            ? Yup.object({
                name: Yup.string().required('Project name is required'),
                stage: Yup.object()
                  .nullable()
                  .required('Project stage is required'),
              })
            : Yup.object({})
        }
        disabled={!editing}
        form={[
          // {
          //   name: 'states',
          //   itemType: 'table_array',
          //   disabled: true,
          //   label: 'Project States',
          //   jItemProps: {
          //     addLabel: 'Add Project State',
          //   },
          //   tableProps: {
          //     onCreate: (values: any) => createProjectState(values),
          //     onEdit: (values: any) => editProjectState(values),
          //     onDelete: (id: number) => deleteProjectState(id),
          //     readOnly: (values: any) =>
          //       values?.subscription === undefined && values?.id > 0,
          //     columns: [
          //       {
          //         name: 'name',
          //         label: 'State *',
          //         itemType: 'textfield',
          //         jItemProps: {
          //           width: '100%',
          //           sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
          //         },
          //         gridProps: { xs: 12 },
          //       },
          //     ],
          //   },
          //   device: downSm ? 'mobile' : 'desktop',
          // },
          {
            name: 'custom-fields',
            itemType: 'typography',
            jItemProps: {
              variant: 'h6',
              sx: { mb: '0 !important', pb: '0 !important' },
            },
            label: <span>Project States</span>,
          },
          // {
          //   name: 'additional-fields-bottom',
          //   label: '',
          //   itemType: 'divider',
          //   gridProps: { sx: { paddingBottom: '0px !important' } },
          // },
          {
            name: 'states',
            itemType: 'fieldarray',
            label: 'Project States',
            jItemProps: {
              addLabel: 'Add Project Stage',
              onCreate: (values: any) => createProjectState(values),
              onEdit: (values: any) => editProjectState(values),
              onDelete: (id: number) => deleteProjectState(id),
              readOnly: (values: any) =>
                values?.subscription === undefined && values?.id > 0,
              accordion: (
                values: any,
                row: any,
                index: number,
                name: string
              ) => {
                if (row.id === 4) {
                  return {
                    name: 'reasons',
                    itemType: 'fieldarray',
                    label: 'Lost Reasons',
                    gridProps: {
                      sx: { paddingTop: '10px !important' },
                    },
                    jItemProps: {
                      details_label: 'Statuses',
                      onCreate: async (
                        values: any,
                        allValues: any,
                        jItem: string,
                        index: any
                      ) => {
                        await createReason({
                          ...values,
                          state: getIn(
                            allValues,
                            jItem.replace('reasons', 'id')
                          ),
                        });
                        await refetchStates();
                      },
                      onEdit: async (values: any) => {
                        await editReason({
                          ...values,
                        });
                        await refetchStates();
                      },
                      onDelete: async (id: number) => {
                        await deleteReason(id);
                        await refetchStates();
                      },
                    },
                    array: [
                      {
                        name: 'name',
                        label: 'Lost Reason *',
                        itemType: 'textfield',
                        // gridProps: { xs: 4 },
                      },
                    ],
                  };
                } else {
                  return null;
                }
              },
            },
            array: [
              {
                name: 'name',
                label: 'State *',
                itemType: 'textfield',
                gridProps: { xs: 12 },
              },
            ],

            device: downSm ? 'mobile' : 'desktop',
          },
          {
            name: 'custom-fields',
            itemType: 'typography',
            jItemProps: {
              variant: 'h6',
              sx: { mb: '0 !important', pb: '0 !important' },
            },
            label: (
              <span>
                Custom Project Stages <small>(with outcomes)</small>
              </span>
            ),
          },
          // {
          //   name: 'additional-fields-bottom',
          //   label: '',
          //   itemType: 'divider',
          //   gridProps: { sx: { paddingBottom: '0px !important' } },
          // },
          {
            name: 'stages',
            itemType: 'fieldarray',
            label: 'Custom Project Stages',
            jItemProps: {
              addLabel: 'Add Project Stage',
              onCreate: (values: any) => createProjectStage(values),
              onEdit: (values: any) => editProjectStage(values),
              onDelete: (id: number) => deleteProjectStage(id),
              accordion: () => ({
                name: 'outcomes',
                itemType: 'fieldarray',
                label: 'Outcomes',
                gridProps: {
                  sx: { paddingTop: '10px !important' },
                },
                jItemProps: {
                  details_label: 'Statuses',
                  onCreate: async (
                    values: any,
                    allValues: any,
                    jItem: string,
                    index: any
                  ) => {
                    await createOutcome({
                      ...values,
                      stage: getIn(allValues, jItem.replace('outcomes', 'id')),
                      state:
                        typeof values.state === 'number'
                          ? values.state
                          : values.state?.id,
                    });
                    await refetchStages();
                  },
                  onEdit: async (values: any) => {
                    await editOutcome({
                      ...values,
                      state: values.state
                        ? typeof values.state === 'number'
                          ? values.state
                          : values.state?.id
                        : null,
                      awarded: values?.awarded || false,
                    });
                    await refetchStages();
                  },
                  onDelete: async (id: number) => {
                    await deleteOutcome(id);
                    await refetchStages();
                  },
                },
                // array: [
                //   {
                //     name: '',
                //     label: '',
                //     itemType: 'fieldarray',
                //     jItemProps: {
                //       paper: {
                //         elevation: 0,
                //       },
                //       grid: {
                //         alignItems: 'center',
                //         // justifyItems: 'stretch',
                //       },
                //     },
                array: [
                  {
                    name: 'name',
                    label: 'Outcome *',
                    itemType: 'textfield',
                    // gridProps: { xs: 4 },
                  },
                  {
                    name: 'state',
                    label: 'Sets project state (optional)',
                    itemType: 'autocomplete',
                    options: states,
                  },
                  // {
                  //   name: 'workflow',
                  //   label: 'Triggers workflow (optional)',
                  //   itemType: 'autocomplete',
                  //   options: ['Win', 'Loss'],
                  //   jItemProps: {
                  //     size: 'small',
                  //     InputProps: {
                  //       startAdornment: (
                  //         // <InputAdornment position="start">
                  //         <Tooltip title={`Preview Template`}>
                  //           <IconButton
                  //             onClick={event => {
                  //               event.stopPropagation();
                  //               event.preventDefault();

                  //               const workflowForm = getWorkflowForm({
                  //                 form: { workflow: { type: 'won' } },
                  //               } as Stage);
                  //               if (workflowForm) {
                  //                 setWorkflowDetails({
                  //                   open: true,
                  //                   form: workflowForm.form,
                  //                   // form: (
                  //                   //   <JFormik
                  //                   //     disabled={true}
                  //                   //     // formRef={alertFormRef}
                  //                   //     initialValues={
                  //                   //       workflowForm.initialValues || {}
                  //                   //     }
                  //                   //     validationSchema={Yup.object().shape({
                  //                   //       reason: Yup.string()
                  //                   //         .nullable()
                  //                   //         .required(`Please add a reason.`),
                  //                   //     })}
                  //                   //     form={workflowForm.form}
                  //                   //   />
                  //                   // ),
                  //                   title: workflowForm.title,
                  //                   // content: workflowForm.content,
                  //                   onClose: async () => {
                  //                     setWorkflowDetails({
                  //                       open: false,
                  //                     });
                  //                   },
                  //                   onConfirm: async () => {},
                  //                 });
                  //               }
                  //             }}
                  //             size="small"
                  //           >
                  //             <InfoOutlined color="secondary" />
                  //           </IconButton>
                  //         </Tooltip>
                  //         // </InputAdornment>
                  //       ),
                  //     },
                  //   },
                  //   // jItemProps: {
                  //   //   helperText: (values: any) => (
                  //   //     <Typography
                  //   //       variant="caption"
                  //   //       id="tableTitle"
                  //   //       component="div"
                  //   //     >
                  //   //       <Tooltip title={`Preview Workflow`}>
                  //   //         <IconButton
                  //   //           onClick={event => {
                  //   //             event.stopPropagation();
                  //   //             event.preventDefault();
                  //   //             const workflowForm = getWorkflowForm({
                  //   //               form: { workflow: 'won' },
                  //   //             } as Stage);
                  //   //             if (workflowForm) {
                  //   //               setWorkflowDetails({
                  //   //                 open: true,
                  //   //                 form: workflowForm.form,
                  //   //                 // form: (
                  //   //                 //   <JFormik
                  //   //                 //     disabled={true}
                  //   //                 //     // formRef={alertFormRef}
                  //   //                 //     initialValues={
                  //   //                 //       workflowForm.initialValues || {}
                  //   //                 //     }
                  //   //                 //     validationSchema={Yup.object().shape({
                  //   //                 //       reason: Yup.string()
                  //   //                 //         .nullable()
                  //   //                 //         .required(`Please add a reason.`),
                  //   //                 //     })}
                  //   //                 //     form={workflowForm.form}
                  //   //                 //   />
                  //   //                 // ),
                  //   //                 title: workflowForm.title,
                  //   //                 // content: workflowForm.content,
                  //   //                 onClose: async () => {},
                  //   //                 onConfirm: async () => {},
                  //   //               });
                  //   //             }
                  //   //           }}
                  //   //           size="small"
                  //   //         >
                  //   //           <InfoOutlined color="secondary" />
                  //   //         </IconButton>
                  //   //       </Tooltip>{' '}
                  //   //       Preview Workflow
                  //   //     </Typography>
                  //   //   ),
                  //   // },
                  // },
                  // {
                  //   name: 'complete',
                  //   label: 'Completes Project',
                  //   itemType: 'checkbox',
                  //   jItemProps: {
                  //     tooltip:
                  //       "The project is considered 'Completed' with this outcome.",
                  //   },
                  //   gridProps: { xs: 3 },
                  // },
                ],
                //   },
                // ],
              }),
            },
            // arrayProps: {
            //   wrap: false,
            // },
            array: [
              {
                name: 'name',
                label: 'Stage *',
                itemType: 'textfield',
                jItemProps: {
                  // width: '100%',
                  // sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                  grid: { xs: 9 },
                },
                gridProps: { xs: 9 },
              },
              {
                name: 'awarded',
                label: 'Awarded',
                itemType: 'checkbox',
                jItemProps: {
                  tooltip:
                    "The project is considered 'Awarded' when in this stage.",
                },
                gridProps: { xs: 3 },
              },
            ],

            device: downSm ? 'mobile' : 'desktop',
          },
        ]}
      />
    </>
  );
};
